import React, { useState, useMemo } from 'react';
import { User, Briefcase, Building2, TrendingUp, Eye, Search, Edit, Send, RefreshCw } from 'lucide-react';
import JobDetailsSubmitModal from '../JobDetailsSubmitModal';
import EditSubmissionStatusModal from '../EditSubmissionStatusModal';
import SubmissionDetailModal from '../SubmissionDetailModal';
import { Badge } from '../ui/badge';
import TeamInfoBanner from './TeamInfoBanner';
import StatusBadge from '../shared/StatusBadge';
import Pagination from '../shared/Pagination';
import MultiSelectFilter from '../shared/MultiSelectFilter';
import ViewToggle from '../shared/ViewToggle';
import useTeamData from '../../hooks/useTeamData';
import useSubmissionsFilter from '../../hooks/useSubmissionsFilter';
import { submissionStatusGroups, jobStatusGroups, generateClientGroups } from '../../utils/filterConfigs';
import { TIMEFRAME_OPTIONS, filterByTimeframe, getTimeframeDescription } from '../../utils/timeframeUtils';

/**
 * SubmissionsTab - Refactored
 * Shows candidates linked to jobs with comprehensive filtering
 * 
 * Features:
 * - Team-aware client filtering
 * - Multi-select filters for submission status, clients, job status
 * - Active pipeline default view
 * - Bulk selection and actions
 */
const SubmissionsTab = ({ candidates, jobs, submissions, selectedUser, myTeam, onRefreshSubmissions }) => {
  // Team data
  const { teamLead } = useTeamData(selectedUser, myTeam);
  
  // ViewToggle state for My Submissions / All Submissions
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'
  
  // Filter state and handlers
  const {
    selectedStatuses,
    selectedClients,
    selectedJobStatuses,
    searchTerm,
    uniqueClients,
    activePipelineStatuses,
    setSearchTerm,
    toggleStatus,
    toggleClient,
    toggleJobStatus
  } = useSubmissionsFilter({ teamLead, selectedUser, submissions, jobs });
  
  // UI state
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTimeframe, setSelectedTimeframe] = useState('this_month'); // Default to This Month
  const itemsPerPage = 20;
  
  // Modal state
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [submitModalJob, setSubmitModalJob] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailSubmission, setDetailSubmission] = useState(null);

  // Check if a submission belongs to the current user
  const isMySubmission = React.useCallback((submission) => {
    if (!selectedUser?.id || !submission) return false;
    
    // Primary ownership: submission's recruiter
    if (submission.recruiter_id === selectedUser.id || submission.submitted_by_id === selectedUser.id) {
      return true;
    }
    
    // Check if submission is for a job assigned to current user
    const job = jobs.find(j => j.id === submission.job_id);
    if (job) {
      if (job.primary_recruiter_id === selectedUser.id || job.recruiter_id === selectedUser.id) {
        return true;
      }
      
      // Check assigned recruiters array in job
      if (job.assigned_recruiters && Array.isArray(job.assigned_recruiters)) {
        return job.assigned_recruiters.some(ar => ar.recruiter_id === selectedUser.id);
      }
    }
    
    return false;
  }, [selectedUser?.id, jobs]);

  // Transform submissions data with job and candidate info
  const submissionData = useMemo(() => {
    // Helper function to normalize job status for consistent filtering
    const normalizeJobStatus = (status) => {
      const statusMap = {
        'accepting_candidates': 'accepting_candidates',
        'Accepting Candidates': 'accepting_candidates',
        'interviewing': 'interviewing', 
        'Interviewing': 'interviewing',
        'filled': 'filled',
        'Filled': 'filled',
        'covered': 'covered',
        'Covered': 'covered',
        'cancelled': 'cancelled',
        'Cancelled': 'cancelled',
        'on_hold': 'on_hold',
        'On Hold': 'on_hold',
        'offer_out': 'offer_out',
        'Offer Out': 'offer_out'
      };
      return statusMap[status] || status?.toLowerCase() || 'unknown';
    };

    return submissions.map(sub => {
      const candidate = candidates.find(c => c.id === sub.candidate_id) || {};
      const job = jobs.find(j => j.id === sub.job_id) || {};
      
      return {
        id: sub.id,
        candidateId: sub.candidate_id,
        candidateName: candidate.name || candidate.full_name || candidate.candidate_name || 'Unknown Candidate',
        candidateEmail: candidate.email || 'N/A',
        jobId: sub.job_id,
        jobTitle: job.title || job.job_title || sub.job_title || 'Unknown Job',
        jobClient: job.client_company || 'N/A',
        jobStatus: normalizeJobStatus(job.status),
        submissionStatus: (sub.submission_status || sub.status || sub.current_status || 'submission').toLowerCase(),
        stage: sub.stage || 'N/A',
        dateAdded: sub.submission_date || sub.date_added || sub.created_at,
        job,
        candidate,
        submission: sub
      };
    });
  }, [submissions, candidates, jobs]);

  // Apply filters (including timeframe)
  const filteredData = useMemo(() => {
    // First filter by timeframe using utility
    const timeframeFiltered = filterByTimeframe(
      submissionData,
      'dateAdded',
      selectedTimeframe
    );
    
    // Then apply other filters
    // NOTE: The client filter (matchesClient) uses inherited clients from team lead
    // for recruiters, ensuring proper data scope without manual client assignment
    return timeframeFiltered.filter(item => {
      const matchesSearch = searchTerm === '' || 
        item.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.jobClient.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = selectedStatuses.size === 0 || 
        selectedStatuses.has(item.submissionStatus.toLowerCase());
      
      // Client filter: selectedClients.size === 0 means "show all clients"
      // When size > 0, only show submissions for selected clients (inherited from team lead)
      const matchesClient = selectedClients.size === 0 || 
        selectedClients.has(item.jobClient);
      
      const matchesJobStatus = selectedJobStatuses.size === 0 || 
        selectedJobStatuses.has(item.jobStatus);
      
      // ViewMode filter - My Submissions vs All Submissions  
      const matchesOwnership = viewMode === 'all' || isMySubmission(item.submission);
      
      return matchesSearch && matchesStatus && matchesClient && matchesJobStatus && matchesOwnership;
    }).sort((a, b) => {
      // Sort by most recent first
      const dateA = new Date(a.dateAdded || 0);
      const dateB = new Date(b.dateAdded || 0);
      return dateB - dateA;
    });
  }, [submissionData, searchTerm, selectedStatuses, selectedClients, selectedJobStatuses, selectedTimeframe]);

  // Pagination
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  return (
    <div className="space-y-4" data-testid="submissions-tab">
      {/* Team Info Banner */}
      <TeamInfoBanner selectedUser={selectedUser} teamLead={teamLead} myTeam={myTeam} />
      
      {/* View Toggle - My Submissions / All Submissions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {viewMode === 'my' ? 'My Submissions' : 'All Submissions'} ({filteredData.length})
          </h3>
          <ViewToggle 
            leftLabel="My Submissions"
            rightLabel="All Submissions"
            activeView={viewMode}
            onToggle={setViewMode}
            leftValue="my"
            rightValue="all"
          />
        </div>
      </div>
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-6 w-6 text-purple-600" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                Current Pipeline ({filteredData.length})
              </h3>
              <p className="text-sm text-gray-600">
                {selectedStatuses.size === activePipelineStatuses.length ? (
                  <span className="text-green-600 font-medium">Active pipeline • </span>
                ) : (
                  `${selectedStatuses.size} submission status{selectedStatuses.size !== 1 ? 'es' : ''} • `
                )}
                {selectedClients.size} client{selectedClients.size !== 1 ? 's' : ''} • {selectedJobStatuses.size} job status{selectedJobStatuses.size !== 1 ? 'es' : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search candidates, jobs, clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="search-input"
            />
          </div>

          {/* Submission Status Filter */}
          <MultiSelectFilter
            label="Submission Status"
            selectedItems={selectedStatuses}
            onToggle={toggleStatus}
            groups={submissionStatusGroups}
            testId="status-filter-button"
          />

          {/* Client Filter */}
          <MultiSelectFilter
            label="Clients"
            selectedItems={selectedClients}
            onToggle={toggleClient}
            groups={generateClientGroups(uniqueClients)}
            testId="client-filter-button"
          />

          {/* Job Status Filter */}
          <MultiSelectFilter
            label="Job Status"
            selectedItems={selectedJobStatuses}
            onToggle={toggleJobStatus}
            groups={jobStatusGroups}
            testId="job-status-filter-button"
          />
        </div>
      </div>

      {/* Timeframe Filter */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">
            Submissions Added: {getTimeframeDescription(selectedTimeframe)}
          </span>
          <button
            onClick={() => {
              if (onRefreshSubmissions) {
                onRefreshSubmissions();
              }
            }}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="refresh-submissions-button"
            title="Refresh submissions data"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {TIMEFRAME_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => {
                setSelectedTimeframe(option.value);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedTimeframe === option.value
                  ? 'bg-purple-600 text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-purple-50 border border-gray-300'
              }`}
              data-testid={`timeframe-${option.value}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="submissions-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Candidate</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Job</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Client Company</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Job Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Submission Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Submission Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                    No submissions found matching your filters
                  </td>
                </tr>
              ) : (
                paginatedData.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      setDetailSubmission(item.submission);
                      setShowDetailModal(true);
                    }}
                    data-testid={`submission-row-${item.id}`}
                  >
                    {/* Submission ID */}
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-1 text-xs font-mono font-semibold bg-green-100 text-green-800 rounded">
                        {item.submission?.display_id || item.id?.substring(0, 8)}
                      </span>
                    </td>
                    
                    {/* Candidate */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <User className="h-8 w-8 text-gray-400 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{item.candidateName}</span>
                            {item.candidate?.display_id && (
                              <span className="text-xs font-mono text-gray-500">({item.candidate.display_id})</span>
                            )}
                            {/* Source Indicators */}
                            {item.submission?.submission_source === 'ai_suggested' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                                AI Sourced
                              </span>
                            )}
                            {item.candidate?.source === 'internal' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0v-6.586A2 2 0 006.828 13L12 7.172l5.172 5.828A2 2 0 0018.5 14.414V21"/>
                                </svg>
                                Internal
                              </span>
                            )}
                            {item.candidate?.source === 'referral' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m3 5.197V9a3 3 0 00-6 0v2.25"/>
                                </svg>
                                Referral
                              </span>
                            )}
                            {!item.submission?.submission_source || item.submission?.submission_source === 'manual' ? (
                              <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                                Manual
                              </span>
                            ) : null}
                            {/* Match Score for AI-sourced candidates */}
                            {item.submission?.submission_source === 'ai_suggested' && item.submission?.match_score && (
                              <span className="inline-flex items-center px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                                {Math.round(item.submission.match_score)}% match
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{item.candidateEmail}</div>
                        </div>
                      </div>
                    </td>
                    {/* Job */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Briefcase className="h-4 w-4 text-gray-400" />
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">{item.jobTitle}</span>
                          {item.job?.display_id && (
                            <span className="text-xs font-mono text-gray-500">{item.job.display_id}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">{item.jobClient}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={item.jobStatus} type="job" />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={item.submissionStatus} type="submission" />
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {item.dateAdded ? new Date(item.dateAdded).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingSubmission(item)}
                          className="text-gray-600 hover:text-gray-800 transition-colors"
                          title="Edit submission status"
                          data-testid={`edit-submission-${item.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setSubmitModalJob(item.job)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="Submit another candidate to this job"
                          data-testid={`submit-another-${item.id}`}
                        >
                          <Send className="h-4 w-4" />
                        </button>
                        <button
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title="View details"
                          data-testid={`view-submission-${item.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredData.length}
          itemsPerPage={itemsPerPage}
        />
      )}
      
      {/* Submit Another Candidate Modal */}
      {submitModalJob && (
        <JobDetailsSubmitModal
          isOpen={true}
          onClose={() => setSubmitModalJob(null)}
          job={submitModalJob}
          onSubmitSuccess={() => {
            setSubmitModalJob(null);
            // Refresh submissions data to show new submission
            if (onRefreshSubmissions) {
              onRefreshSubmissions();
            }
          }}
        />
      )}
      
      {/* Edit Submission Modal */}
      {editingSubmission && (
        <EditSubmissionStatusModal
          isOpen={true}
          onClose={() => setEditingSubmission(null)}
          submission={editingSubmission}
          selectedUser={selectedUser}
          onSaveSuccess={() => {
            setEditingSubmission(null);
            // Refresh submissions data to show updated status
            if (onRefreshSubmissions) {
              onRefreshSubmissions();
            }
          }}
        />
      )}

      {/* Submission Detail Modal */}
      <SubmissionDetailModal
        submission={detailSubmission}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setDetailSubmission(null);
        }}
        onEdit={(submission) => {
          setShowDetailModal(false);
          setEditingSubmission(submission);
        }}
      />
    </div>
  );
};

export default SubmissionsTab;
